import React from 'react';
import { cn } from '@/lib/utils';

interface CircularProgressProps {
  value: number;
  size?: 'sm' | 'md' | 'lg';
  strokeWidth?: number;
  showLabel?: boolean;
  className?: string;
  labelClassName?: string;
}

const sizeMap = {
  sm: 48,
  md: 80,
  lg: 120,
};

export const CircularProgress: React.FC<CircularProgressProps> = ({
  value,
  size = 'md',
  strokeWidth = 6,
  showLabel = true,
  className,
  labelClassName,
}) => {
  const numericSize = sizeMap[size];
  const radius = (numericSize - strokeWidth) / 2;
  const circumference = 2 * Math.PI * radius;
  const progress = Math.min(100, Math.max(0, value));
  const offset = circumference - (progress / 100) * circumference;

  const getProgressColor = (val: number) => {
    if (val === 100) return 'stroke-green-500';
    if (val >= 75) return 'stroke-emerald-500';
    if (val >= 50) return 'stroke-primary';
    if (val >= 25) return 'stroke-amber-500';
    return 'stroke-muted-foreground/30';
  };

  return (
    <div className={cn('relative inline-flex items-center justify-center', className)}>
      <svg
        width={numericSize}
        height={numericSize}
        className="transform -rotate-90"
      >
        {/* Background circle */}
        <circle
          cx={numericSize / 2}
          cy={numericSize / 2}
          r={radius}
          strokeWidth={strokeWidth}
          className="stroke-muted fill-none"
        />
        {/* Progress circle */}
        <circle
          cx={numericSize / 2}
          cy={numericSize / 2}
          r={radius}
          strokeWidth={strokeWidth}
          strokeLinecap="round"
          className={cn('fill-none transition-all duration-500 ease-out', getProgressColor(progress))}
          style={{
            strokeDasharray: circumference,
            strokeDashoffset: offset,
          }}
        />
      </svg>
      {showLabel && (
        <span
          className={cn(
            'absolute font-bold',
            size === 'sm' && 'text-xs',
            size === 'md' && 'text-lg',
            size === 'lg' && 'text-2xl',
            labelClassName
          )}
        >
          {Math.round(progress)}%
        </span>
      )}
    </div>
  );
};

export default CircularProgress;
